import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import { ArrowUpRight, ArrowRight, Check, Sparkles } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { trackLead } from "@/lib/pixel";
import { SmoothScroll } from "@/components/SmoothScroll";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EASE = [0.16, 1, 0.3, 1];

const STATES = [
  "Idea", "Questionario", "Struttura", "Scrittura", "Revisione",
  "Copertina", "Impaginazione", "Pubblicazione", "Amazon", "Royalty",
];

const CHAPTERS = [
  { n: "01", t: "Racconti la tua idea", d: "Un questionario guidato, una domanda alla volta. Nessun modulo infinito, nessun PDF da compilare." },
  { n: "02", t: "Il team lavora con te", d: "Struttura, scrittura e revisione avanzano sotto i tuoi occhi. Ogni aggiornamento arriva qui, non su WhatsApp." },
  { n: "03", t: "Approvi e pubblichi", d: "Approva i capitoli, scegli la copertina, paga in sicurezza. Il tuo libro arriva su Amazon." },
  { n: "04", t: "Monitori le royalty", d: "Vendite e guadagni in tempo reale, in un'unica dashboard elegante e chiara." },
];

const PACKAGES = [
  { name: "Professional", price: "€497", featured: true, launch: ["€199", "€247"], features: [
    "Copertina professionale del libro",
    "Editing professionale completo",
    "Impaginazione avanzata",
    "Gestione vendite su Amazon · provvigioni 50%",
    "Pagamenti ogni 2 mesi + monitoraggio recensioni",
  ] },
];

const MaskLine = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

const Header = () => (
  <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#050A15]/60 backdrop-blur-xl">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
      <Logo />
      <nav className="hidden items-center gap-10 text-sm text-white/60 md:flex">
        <a href="#metodo" className="transition-colors hover:text-white" data-testid="nav-metodo">Metodo</a>
        <a href="#percorso" className="transition-colors hover:text-white" data-testid="nav-percorso">Percorso</a>
        <a href="#prezzi" className="transition-colors hover:text-white" data-testid="nav-prezzi">Prezzi</a>
        <a href="#faq" className="transition-colors hover:text-white" data-testid="nav-faq">FAQ</a>
        <a href="#analisi" className="transition-colors hover:text-white" data-testid="nav-analisi">Analisi gratuita</a>
      </nav>
      <div className="flex items-center gap-3">
        <Link to="/login" data-testid="header-login-link" className="text-sm text-white/70 transition-colors hover:text-white">
          Accedi
        </Link>
        <Link to="/register" data-testid="header-cta-btn">
          <Button className="rounded-full bg-[#D4AF37] px-4 text-[#050A15] hover:bg-[#E5C158] sm:px-5">
            Inizia ora
          </Button>
        </Link>
      </div>
    </div>
  </header>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden pt-32">
      <motion.div style={{ y: imgY }} className="pointer-events-none absolute right-0 top-24 h-[70vh] w-[45vw] max-w-[640px]">
        <div className="relative h-full w-full overflow-hidden rounded-bl-[80px] border border-white/5">
          <img
            src="https://images.unsplash.com/photo-1519682337058-a94d519337bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Libro aperto su scrivania in legno"
            className="h-full w-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#050A15]/40 to-[#050A15]" />
        </div>
      </motion.div>

      <motion.div style={{ y, opacity }} className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-8 text-xs uppercase tracking-[0.35em] text-[#D4AF37]"
          data-testid="hero-eyebrow"
        >
          La casa editrice digitale per autori indipendenti
        </motion.p>

        <h1 className="font-serif text-6xl font-light leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-[8.5rem]">
          <MaskLine delay={0.15}>Il tuo libro,</MaskLine>
          <MaskLine delay={0.3}>
            <span className="italic text-[#D4AF37]">dall'idea</span> ad
          </MaskLine>
          <MaskLine delay={0.45}>Amazon.</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.8 }}
          className="mt-12 flex max-w-xl flex-col gap-8"
        >
          <p className="text-lg leading-relaxed text-white/60">
            Un'unica piattaforma per scrivere, revisionare e pubblicare il tuo libro.
            Niente più email, niente più WhatsApp, niente più PDF dispersi.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="hero-cta-btn">
              <Button className="group h-12 rounded-full bg-[#D4AF37] px-7 text-[#050A15] hover:bg-[#E5C158]">
                Crea il tuo progetto
                <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <a href="#percorso" data-testid="hero-secondary-link" className="group inline-flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-white">
              Scopri il percorso
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

const MarqueeBand = () => (
  <div className="border-y border-white/5 bg-[#0A1124] py-6">
    <Marquee speed={40} gradient={false} autoFill>
      {["Niente più email.", "Niente più PDF.", "Niente più WhatsApp.", "Solo il tuo libro."].map((t, i) => (
        <span key={i} className="mx-8 font-serif text-3xl italic text-white/30 sm:text-4xl">
          {t}
          <span className="mx-8 text-[#D4AF37]">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const Manifesto = () => (
  <section id="metodo" className="mx-auto max-w-7xl px-6 py-32 lg:px-10">
    <Reveal>
      <p className="mb-4 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Il metodo</p>
      <h2 className="max-w-3xl font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
        Quattro passi, dalla prima idea al tuo primo lettore.
      </h2>
    </Reveal>
    <div className="mt-20 grid gap-px overflow-hidden rounded-2xl border border-white/5 bg-white/5 md:grid-cols-2">
      {CHAPTERS.map((c, i) => (
        <Reveal key={c.n} delay={i * 0.08}>
          <div className="group h-full bg-[#0A1124] p-10 transition-colors duration-300 hover:bg-[#111A30]">
            <span className="font-mono text-sm text-[#D4AF37]">{c.n}</span>
            <h3 className="mt-6 font-serif text-3xl font-light text-white">{c.t}</h3>
            <p className="mt-4 text-white/50">{c.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const TimelineShowcase = () => (
  <section id="percorso" className="border-y border-white/5 bg-[#0A1124] py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <p className="mb-4 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Il percorso</p>
        <h2 className="max-w-3xl font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
          Dieci tappe. Sempre sotto controllo.
        </h2>
        <p className="mt-6 max-w-xl text-white/50">
          Ogni progetto ha una timeline visiva. Sai sempre a che punto sei e cosa succederà dopo.
        </p>
      </Reveal>
      <div className="mt-16 flex flex-wrap gap-3">
        {STATES.map((s, i) => (
          <Reveal key={s} delay={i * 0.05}>
            <div className="flex items-center gap-3 rounded-full border border-white/10 bg-[#050A15] px-5 py-3">
              <span className="font-mono text-xs text-[#D4AF37]">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm text-white/80">{s}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section id="chi-siamo" className="mx-auto max-w-5xl px-6 pt-32 pb-12 lg:px-10">
    <Reveal>
      <p className="mb-6 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Chi sono</p>
      <h2 className="font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
        Non devi essere uno scrittore.
        <br />
        <span className="text-[#D4AF37] italic">Devi solo avere qualcosa che vale la pena raccontare.</span>
      </h2>
    </Reveal>
    <Reveal delay={0.15}>
      <div className="mt-10 space-y-6 font-serif text-lg leading-relaxed text-white/70 sm:text-xl">
        <p>
          <span className="text-white">Hai un'idea, una storia o un'esperienza che merita di diventare un libro,</span>{" "}
          ma non sai da dove iniziare?
        </p>
        <p>
          Con <span className="text-[#D4AF37]">oltre 100 libri pubblicati</span>, aiuto autori, imprenditori e
          professionisti a trasformare conoscenze ed esperienze in libri professionali.
        </p>
        <p>
          Possiamo partire anche da una semplice idea. Costruiremo insieme struttura, capitoli e contenuti
          mantenendo <span className="text-white">autentica la tua voce</span>.
        </p>
        <p>
          Posso seguirti dalla progettazione al ghostwriting, revisione, copertina, impaginazione e preparazione per
          Amazon KDP.
        </p>
        <p>
          Ho sviluppato progetti per i mercati italiano e internazionale, inclusi libri arrivati al
          <span className="text-[#D4AF37]"> #1 Best Seller su Amazon</span>.
        </p>
      </div>
    </Reveal>
  </section>
);

const FAQ_ITEMS = [
  { q: "Devo avere già scritto il libro?",
    a: "No. Possiamo partire anche da una semplice idea, dalla tua esperienza o dalle tue conoscenze. Ti aiuterò a trasformarle in una struttura professionale." },
  { q: "Come fai a scrivere un libro che sembri veramente mio?",
    a: "Prima di scrivere raccolgo la tua storia, le tue idee e il tuo modo di comunicare. L'obiettivo è mantenere la tua voce, non sostituirla con la mia." },
  { q: "Quali tipi di libri puoi realizzare?",
    a: "Business, biografie, crescita personale, formazione, real estate, psicologia, guide professionali e altri progetti nonfiction." },
  { q: "Puoi occuparti anche della copertina e dell'impaginazione?",
    a: "Sì. In base al pacchetto scelto posso seguire anche cover, revisione, impaginazione e preparazione dei file." },
  { q: "Puoi aiutarmi a pubblicare su Amazon KDP?",
    a: "Sì. Posso preparare il progetto per Amazon KDP e assisterti nelle diverse fasi della pubblicazione." },
  { q: "Utilizzi l'intelligenza artificiale?",
    a: "Utilizzo strumenti di AI per rendere il processo più efficiente, ma struttura, sviluppo, revisione e controllo editoriale vengono gestiti professionalmente per ottenere un risultato coerente e personalizzato." },
  { q: "Posso iniziare con il pacchetto Base e continuare successivamente?",
    a: "Assolutamente sì. È proprio pensato per questo: possiamo partire dalla struttura del tuo libro e successivamente sviluppare capitoli o l'intero progetto." },
  { q: "Perché scegliere il tuo servizio?",
    a: "Perché non vendo semplicemente parole. Ho contribuito alla realizzazione di oltre 100 libri e seguo il progetto dall'idea al prodotto editoriale finale." },
];

const Faq = () => (
  <section id="faq" className="mx-auto max-w-5xl px-6 py-24 lg:px-10">
    <Reveal>
      <p className="mb-6 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Domande frequenti</p>
      <h2 className="font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
        Le risposte alle domande più comuni.
      </h2>
      <p className="mt-4 text-white/50">Se non trovi quello che cerchi, scrivimelo nel form qui sotto.</p>
    </Reveal>
    <Reveal delay={0.1}>
      <Accordion type="single" collapsible className="mt-12 divide-y divide-white/5 rounded-2xl border border-white/5 bg-[#0A1124]">
        {FAQ_ITEMS.map((item, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="border-b-0 px-6" data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="py-6 text-left font-serif text-lg font-light text-white hover:no-underline sm:text-xl [&[data-state=open]>svg]:text-[#D4AF37]">
              <span className="flex items-center gap-4 pr-4">
                <span className="text-[#D4AF37]/60 font-sans text-sm">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {item.q}
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 pl-10 text-base leading-relaxed text-white/70">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Reveal>
  </section>
);

const LatestReleases = () => {
  const [releases, setReleases] = useState([]);

  useEffect(() => {
    api.get("/home-releases")
      .then(({ data }) => setReleases(data.releases || []))
      .catch(() => {});
  }, []);

  if (releases.length === 0) return null;

  return (
    <section id="uscite" className="border-t border-white/5 bg-[#0A1124] py-32" data-testid="releases-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="mb-6 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Ultime uscite</p>
          <h2 className="font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
            Gli ultimi libri pubblicati.
          </h2>
          <p className="mt-4 text-white/50">Tocca una copertina per vedere il libro su Amazon.</p>
        </Reveal>
        <div className="mt-16 grid grid-cols-2 gap-6 sm:grid-cols-3 xl:grid-cols-6">
          {releases.map((r, i) => (
            <Reveal key={r.release_id} delay={i * 0.05}>
              <a
                href={r.amazon_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
                data-testid={`release-card-${r.release_id}`}
              >
                <div className="overflow-hidden rounded-xl border border-white/5">
                  <img
                    src={`${API}/home-releases/${r.release_id}/cover`}
                    alt={`Copertina di ${r.title}`}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                    data-testid={`release-cover-${r.release_id}`}
                  />
                </div>
                <p className="mt-4 font-serif text-lg font-light leading-snug text-white">{r.title}</p>
                <span
                  className="mt-1 inline-flex items-center gap-1 text-xs text-[#D4AF37]/80 transition-colors group-hover:text-[#D4AF37]"
                  data-testid={`release-amazon-link-${r.release_id}`}
                >
                  Vedi su Amazon <ArrowUpRight className="h-3 w-3" />
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Testimonial = () => (
  <section className="mx-auto max-w-7xl px-6 py-32 lg:px-10">
    <div className="grid items-center gap-12 md:grid-cols-12">
      <Reveal className="md:col-span-5">
        <div className="relative overflow-hidden rounded-2xl border border-white/5">
          <img
            src="/testimonial-cover-filippo-pasta.jpg"
            alt='Copertina del libro "La musica è vita" di Filippo Pasta'
            className="aspect-[4/5] w-full object-cover"
            data-testid="testimonial-cover-img"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050A15] via-transparent to-transparent" />
        </div>
      </Reveal>
      <Reveal delay={0.15} className="md:col-span-7">
        <blockquote className="font-serif text-3xl font-light leading-snug text-white sm:text-4xl">
          “Per la prima volta ho visto il mio libro prendere forma senza rincorrere nessuno.
          <span className="text-[#D4AF37]"> Tutto in un posto solo.</span>”
        </blockquote>
        <div className="mt-8 flex items-center gap-4">
          <div className="h-px w-12 bg-[#D4AF37]" />
          <div>
            <p className="text-white" data-testid="testimonial-author-name">Filippo Pasta</p>
            <p className="text-sm text-white/40">Autore · "La musica è vita"</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Pricing = () => {
  const navigate = useNavigate();
  return (
    <section id="prezzi" className="border-t border-white/5 bg-[#0A1124] py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">L'offerta</p>
          <h2 className="font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
            Un unico percorso. Tutto incluso.
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-6 md:mx-auto md:max-w-xl">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <div
                className={`flex h-full flex-col rounded-2xl border p-8 transition-transform duration-300 hover:-translate-y-1 ${
                  p.featured ? "border-[#D4AF37]/40 bg-[#111A30]" : "border-white/5 bg-[#050A15]"
                }`}
                data-testid={`pricing-card-${p.name}`}
              >
                {p.featured && (
                  <span className="mb-4 w-fit rounded-full bg-[#D4AF37]/15 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">
                    Tutto incluso
                  </span>
                )}
                <h3 className="font-serif text-2xl text-white">{p.name}</h3>
                <p className="mt-4 text-sm text-white/40" data-testid="launch-prices">
                  Prezzi di lancio terminati:{" "}
                  {p.launch.map((lp) => (
                    <span key={lp} className="mr-2 line-through decoration-[#D4AF37]/60">{lp}</span>
                  ))}
                </p>
                <p className="mt-2 font-serif text-5xl font-light text-white">{p.price}</p>
                <ul className="mt-8 flex flex-1 flex-col gap-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-white/60">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate("/register")}
                  data-testid={`pricing-cta-${p.name}`}
                  className={`mt-8 rounded-full ${
                    p.featured
                      ? "bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]"
                      : "bg-white/5 text-white hover:bg-white/10"
                  }`}
                >
                  Inizia con {p.name}
                </Button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const LeadForm = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", profession: "", book_about: "", publication: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      trackLead({ content_name: "Analisi Gratuita del Tuo Libro" });
      toast.success("Richiesta inviata! Ti ricontatteremo presto.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="analisi" className="border-t border-white/5 bg-[#0A1124] py-32">
      <div className="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-2 lg:px-10">
        <Reveal>
          <p className="mb-4 flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
            <Sparkles className="h-3.5 w-3.5" /> Analisi gratuita
          </p>
          <h2 className="font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
            Analisi gratuita del tuo libro.
          </h2>
          <p className="mt-6 max-w-md text-white/50">
            Raccontaci di te con calma: non esistono risposte giuste o sbagliate. Analizzeremo il
            materiale e realizzeremo <span className="text-white">gratuitamente</span> la struttura del
            libro e i primi 10 capitoli. Poi deciderai liberamente se proseguire con SelfBook Italia.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          {done ? (
            <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-[#D4AF37]/30 bg-[#050A15] p-10 text-center" data-testid="lead-success">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#D4AF37]/15 text-[#D4AF37]">
                <Check className="h-7 w-7" />
              </span>
              <p className="mt-6 font-serif text-2xl font-light text-white">Grazie!</p>
              <p className="mt-2 text-white/50">Abbiamo ricevuto la tua richiesta. Il nostro team ti ricontatterà molto presto.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-white/5 bg-[#050A15] p-8" data-testid="lead-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label className="text-white/70">Nome e Cognome</Label>
                  <Input required data-testid="lead-name" value={form.name} onChange={set("name")} placeholder="Il tuo nome" className="mt-1.5 border-white/10 bg-white/5 text-white placeholder:text-white/30" />
                </div>
                <div>
                  <Label className="text-white/70">Email</Label>
                  <Input required type="email" data-testid="lead-email" value={form.email} onChange={set("email")} placeholder="nome@email.it" className="mt-1.5 border-white/10 bg-white/5 text-white placeholder:text-white/30" />
                </div>
                <div>
                  <Label className="text-white/70">Telefono</Label>
                  <Input required data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="+39 ..." className="mt-1.5 border-white/10 bg-white/5 text-white placeholder:text-white/30" />
                </div>
                <div>
                  <Label className="text-white/70">Città</Label>
                  <Input data-testid="lead-city" value={form.city} onChange={set("city")} placeholder="Città" className="mt-1.5 border-white/10 bg-white/5 text-white placeholder:text-white/30" />
                </div>
                <div>
                  <Label className="text-white/70">Professione</Label>
                  <Input data-testid="lead-profession" value={form.profession} onChange={set("profession")} placeholder="La tua professione" className="mt-1.5 border-white/10 bg-white/5 text-white placeholder:text-white/30" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-white/70">Di cosa parlerà il tuo libro?</Label>
                  <Textarea data-testid="lead-book-about" value={form.book_about} onChange={set("book_about")} rows={3} placeholder="Racconta in breve la tua idea..." className="mt-1.5 border-white/10 bg-white/5 text-white placeholder:text-white/30" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-white/70">Come desideri pubblicare?</Label>
                  <Select value={form.publication} onValueChange={(v) => setForm((f) => ({ ...f, publication: v }))}>
                    <SelectTrigger data-testid="lead-publication" className="mt-1.5 border-white/10 bg-white/5 text-white">
                      <SelectValue placeholder="Scegli un'opzione" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#0A1124] text-white">
                      {["Solo libro", "Amazon", "SelfBook con royalty condivise"].map((o) => (
                        <SelectItem key={o} value={o} className="focus:bg-white/5 focus:text-white">{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={busy} data-testid="lead-submit" className="group mt-6 h-12 w-full rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
                {busy ? "Invio…" : "Richiedi l'analisi gratuita"}
                {!busy && <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />}
              </Button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

const FinalCTA = () => (
  <section className="mx-auto max-w-7xl px-6 py-40 text-center lg:px-10">
    <Reveal>
      <h2 className="mx-auto max-w-4xl font-serif text-5xl font-light leading-[0.95] text-white sm:text-7xl">
        La tua storia merita di essere <span className="italic text-[#D4AF37]">un libro</span>.
      </h2>
      <Link to="/register" data-testid="final-cta-btn" className="mt-12 inline-block">
        <Button className="group h-14 rounded-full bg-[#D4AF37] px-9 text-base text-[#050A15] hover:bg-[#E5C158]">
          Inizia gratis oggi
          <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
        </Button>
      </Link>
    </Reveal>
  </section>
);

const Footer = () => (
  <footer className="border-t border-white/5 py-12">
    <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 sm:flex-row lg:px-10">
      <Logo />
      <p className="text-sm text-white/30">© {new Date().getFullYear()} SelfBook Italia. Tutti i diritti riservati.</p>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <SmoothScroll>
      <div className="relative bg-ink">
        <Header />
        <Hero />
        <MarqueeBand />
        <About />
        <LatestReleases />
        <Faq />
        <Manifesto />
        <TimelineShowcase />
        <Testimonial />
        <Pricing />
        <LeadForm />
        <FinalCTA />
        <Footer />
      </div>
    </SmoothScroll>
  );
}
